import { useMutation, useQuery } from '@zg-rentals/react-query5';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { cloneDeep } from 'es-toolkit';

import makeRequest from '../../utils/makeRequestUtils';
import useRequestConfig from '../../hooks/useRequestConfig';
import { RENTAL_MANAGER_API_V1_PREFIX } from '../../constants/RouteConstants';
import StringConstants from '../../constants/StringConstants';
import PropertiesActions from '../../actions/PropertiesActions';

type UseGetDisclaimersDataProps = {
  listingAlias: string;
  disclaimerTypes: string;
};

/**
 *
 * @param { listingAlias, disclaimerTypes } queryParams
 * one or more of:
 *    full, generalV2, coverageV2, coverageShort, premiumGeneral, premiumSpecific, entity, dwelling
 *
 */
const useGetDisclaimers = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const queryFn = (data: UseGetDisclaimersDataProps) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getDisclaimers`,
      requestConfig,
      config: { data: { listingAlias: data.listingAlias, disclaimerTypes: data.disclaimerTypes } },
    });

  return useMutation({ mutationFn: (data: UseGetDisclaimersDataProps) => queryFn(data), ...mutationOptions });
};

const useGetRentalProtectionStateForListing = (listingId: string, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionStateForListing`,
      requestConfig,
      config: { params: { listingAlias: listingId } },
    });

  return useQuery({ queryKey: ['rentalProtectionStateForListing', listingId], queryFn, ...queryOptions });
};

const useGetRentalProtectionStateForListingMultiPolicy = (listingId: string, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionStateForListingMultiPolicy`,
      requestConfig,
      config: { params: { listingAlias: listingId } },
    });

  return useQuery({ queryKey: ['rentalProtectionStateForListingMultiPolicy', listingId], queryFn, ...queryOptions });
};

type UseGetRentalProtectionManagePageMultiplePoliciesParams = {
  listingAlias?: string;
  disclaimerType?: string;
  stepOffset?: string;
};

const useGetRentalProtectionManagePageMultiplePolicies = (
  queryParams: UseGetRentalProtectionManagePageMultiplePoliciesParams = {},
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const { listingAlias, disclaimerType, stepOffset } = queryParams;

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionManagePageMultiplePolicies`,
      requestConfig,
      config: { params: { listingAlias, disclaimerType, stepOffset } },
    });

  return useQuery({
    queryKey: ['rentalProtectionManagePageMultiplePolicies', listingAlias, disclaimerType, stepOffset],
    queryFn,
    // only eligible to refetch after the following amount of time
    staleTime: Infinity,
    ...queryOptions,
  });
};

type UseGetRentalProtectionProductDetailsContentParams = {
  listingAlias?: string;
  listingAddressState?: string;
};

const useGetRentalProtectionProductDetailsContent = (
  queryParams: UseGetRentalProtectionProductDetailsContentParams = {},
  queryOptions = {},
) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const { listingAlias, listingAddressState } = queryParams;
  const version = 'v2';

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getRentalProtectionProductDetailsContent`,
      requestConfig,
      config: { params: { listingAlias, listingAddressState, version } },
    });

  return useQuery({
    queryKey: ['useGetRentalProtectionProductDetailsContent', listingAlias],
    queryFn,
    // only eligible to refetch after the following amount of time
    staleTime: Infinity,
    ...queryOptions,
  });
};

type UseCompleteLightweightOptInParams = {
  listingAlias: string;
};

const useCompleteLightweightOptIn = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const queryFn = (data: UseCompleteLightweightOptInParams) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/completeLightweightOptIn`,
      requestConfig,
      config: {
        data: { listingAlias: data.listingAlias },
      },
    });

  return useMutation({ mutationFn: (data: UseCompleteLightweightOptInParams) => queryFn(data), ...mutationOptions });
};

type UseUpdateSecurityDepositParams = {
  listingAlias: string;
  securityDeposit: string;
};

const useUpdateSecurityDeposit = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = ({ listingAlias, securityDeposit }: UseUpdateSecurityDepositParams) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/securityDeposit/update`,
      requestConfig,
      config: {
        params: {
          propertyId: listingAlias,
          securityDeposit,
        },
      },
    });

  return useMutation({ mutationFn: (data: UseUpdateSecurityDepositParams) => queryFn(data), ...mutationOptions });
};

type UseUpdateRentPriceParams = {
  floorPlanId: string;
  rentPrice: string;
};

const useUpdateRentPrice = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = ({ floorPlanId, rentPrice }: UseUpdateRentPriceParams) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/price`,
      requestConfig,
      config: {
        params: {
          floorPlanId,
          price: rentPrice,
        },
      },
    });

  return useMutation({ mutationFn: (data: UseUpdateRentPriceParams) => queryFn(data), ...mutationOptions });
};

type UseUpdateSecurityDepositAndRentPriceParams = {
  listingAlias: string;
  securityDeposit: string;
  floorPlanId: string;
  rentPrice: string;
};

const useUpdateSecurityDepositAndRentPrice = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const { mutateAsync: updateSecurityDeposit } = useUpdateSecurityDeposit();
  const { mutateAsync: updateRentPrice } = useUpdateRentPrice();

  const queryFn = ({
    listingAlias,
    securityDeposit,
    floorPlanId,
    rentPrice,
  }: UseUpdateSecurityDepositAndRentPriceParams) =>
    Promise.allSettled([
      updateSecurityDeposit({
        listingAlias,
        securityDeposit,
      }),
      updateRentPrice({
        floorPlanId,
        rentPrice,
      }),
    ]);

  const {
    properties: {
      currentProperty: { floorplans },
    },
    // @ts-expect-error
  } = useSelector((state) => ({ properties: state.properties }), shallowEqual);

  return useMutation({
    mutationFn: (data: UseUpdateSecurityDepositAndRentPriceParams) => queryFn(data),
    ...mutationOptions,
    onSuccess: (responseArr, variables) => {
      if (responseArr.every((res) => res.status === 'fulfilled')) {
        const rentPrice = parseInt(variables.rentPrice);
        const securityDeposit = parseInt(variables.securityDeposit);

        let copyFloorplan;
        if (floorplans.length > 0) {
          copyFloorplan = cloneDeep(floorplans);
          copyFloorplan[0].securityDeposit = securityDeposit;
        }
        dispatch(PropertiesActions.updateCurrentProperty({ securityDeposit, floorplans: copyFloorplan }));
        dispatch(PropertiesActions.updateRentPriceForListing(rentPrice));
      }
    },
  });
};

type UseCancelRentalProtectionPolicyParams = {
  policyId: string;
  listingAlias: string;
  userToken: string;
  endCoverageDate: string;
};

const useCancelRentalProtectionPolicy = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const queryFn = ({ policyId, listingAlias, userToken, endCoverageDate }: UseCancelRentalProtectionPolicyParams) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/cancelRentalProtectionPolicy`,
      requestConfig,
      config: {
        data: {
          policyId,
          listingAlias,
          userToken,
          endCoverageDate,
        },
      },
    });

  return useMutation<unknown, unknown, UseCancelRentalProtectionPolicyParams>({
    mutationFn: (data: UseCancelRentalProtectionPolicyParams) => queryFn(data),
    ...mutationOptions,
  });
};

export {
  useGetDisclaimers,
  useGetRentalProtectionProductDetailsContent,
  useGetRentalProtectionStateForListing,
  useGetRentalProtectionManagePageMultiplePolicies,
  useGetRentalProtectionStateForListingMultiPolicy,
  useCompleteLightweightOptIn,
  useUpdateSecurityDepositAndRentPrice,
  useCancelRentalProtectionPolicy,
};
